import { useState, useCallback, useEffect, useRef } from 'react';
import { cn, isVideoUrl } from '@/lib/utils';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Play, RotateCcw } from 'lucide-react';
import { trackUIEvent } from '@/lib/telemetry/uiEvents';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

interface ProductGalleryProps {
    images: string[];
    title: string;
    productId?: string;
}

// Zoom state for lightbox
interface ZoomState {
    scale: number;
    translateX: number;
    translateY: number;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

/**
 * ProductGallery component with thumbnails, lightbox, and zoom functionality.
 * Features:
 * - Magnifying glass on hover for main image
 * - Lightbox with pinch-to-zoom and double-tap zoom
 * - Pan/drag when zoomed in
 * - Keyboard navigation and touch gestures
 */
export const ProductGallery = ({ images, title, productId }: ProductGalleryProps) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const lightboxTriggerRef = useRef<HTMLButtonElement | null>(null);

    // Magnifier state for main image
    const [showMagnifier, setShowMagnifier] = useState(false);
    const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0, bgX: 0, bgY: 0 });
    const mainImageRef = useRef<HTMLDivElement>(null);

    // Lightbox zoom state
    const [zoomState, setZoomState] = useState<ZoomState>({ scale: 1, translateX: 0, translateY: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [lastPinchDistance, setLastPinchDistance] = useState<number | null>(null);
    const [lastTapTime, setLastTapTime] = useState(0);
    const lightboxImageRef = useRef<HTMLDivElement>(null);

    const selectedMedia = images[selectedIndex] || '';
    const isSelectedVideo = isVideoUrl(selectedMedia);

    // Reset zoom when changing images or closing lightbox
    const resetZoom = useCallback(() => {
        setZoomState({ scale: 1, translateX: 0, translateY: 0 });
    }, []);

    // Track media changes
    const handleImageChange = useCallback((newIndex: number) => {
        setSelectedIndex(newIndex);
        resetZoom();
        trackUIEvent.galleryImageViewed(newIndex, productId);
    }, [productId, resetZoom]);

    const handleLightboxOpenChange = useCallback((open: boolean) => {
        setIsLightboxOpen(open);
        if (open) {
            trackUIEvent.modalOpened('product_gallery_lightbox');
            return;
        }

        resetZoom();
        lightboxTriggerRef.current?.focus();
        trackUIEvent.modalClosed('product_gallery_lightbox');
    }, [resetZoom]);

    const goToPrevious = useCallback(() => {
        const newIndex = selectedIndex > 0 ? selectedIndex - 1 : images.length - 1;
        handleImageChange(newIndex);
    }, [images.length, selectedIndex, handleImageChange]);

    const goToNext = useCallback(() => {
        const newIndex = selectedIndex < images.length - 1 ? selectedIndex + 1 : 0;
        handleImageChange(newIndex);
    }, [images.length, selectedIndex, handleImageChange]);

    // Magnifier handlers for main image
    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!mainImageRef.current || isSelectedVideo) return;

        const rect = mainImageRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate position as percentage
        const xPercent = (x / rect.width) * 100;
        const yPercent = (y / rect.height) * 100;

        setMagnifierPosition({
            x: e.clientX,
            y: e.clientY,
            bgX: xPercent,
            bgY: yPercent,
        });
    }, [isSelectedVideo]);

    const handleMouseEnter = useCallback(() => {
        if (!isSelectedVideo) {
            setShowMagnifier(true);
        }
    }, [isSelectedVideo]);

    const handleMouseLeave = useCallback(() => {
        setShowMagnifier(false);
    }, []);

    // Lightbox zoom controls
    const handleZoomIn = useCallback(() => {
        setZoomState(prev => ({
            ...prev,
            scale: Math.min(prev.scale + ZOOM_STEP, MAX_ZOOM),
        }));
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoomState(prev => {
            const newScale = Math.max(prev.scale - ZOOM_STEP, MIN_ZOOM);
            // Reset position if zooming back to 1x
            if (newScale === 1) {
                return { scale: 1, translateX: 0, translateY: 0 };
            }
            return { ...prev, scale: newScale };
        });
    }, []);

    // Double tap/click to zoom
    const handleDoubleTap = useCallback((clientX: number, clientY: number) => {
        if (!lightboxImageRef.current) return;

        const rect = lightboxImageRef.current.getBoundingClientRect();
        const x = clientX - rect.left - rect.width / 2;
        const y = clientY - rect.top - rect.height / 2;

        setZoomState(prev => {
            if (prev.scale > 1) {
                // Zoom out to 1x
                return { scale: 1, translateX: 0, translateY: 0 };
            }
            // Zoom in to 2x centered on tap position
            return {
                scale: 2,
                translateX: -x,
                translateY: -y,
            };
        });
    }, []);

    // Mouse drag for panning when zoomed
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (zoomState.scale <= 1) return;
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - zoomState.translateX, y: e.clientY - zoomState.translateY });
    }, [zoomState.scale, zoomState.translateX, zoomState.translateY]);

    const handleMouseMoveZoom = useCallback((e: React.MouseEvent) => {
        if (!isDragging || zoomState.scale <= 1) return;

        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        // Constrain panning to image bounds
        const maxPan = (zoomState.scale - 1) * 150; // Approximate constraint
        setZoomState(prev => ({
            ...prev,
            translateX: Math.max(-maxPan, Math.min(maxPan, newX)),
            translateY: Math.max(-maxPan, Math.min(maxPan, newY)),
        }));
    }, [isDragging, dragStart, zoomState.scale]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Touch handlers for lightbox (pinch zoom + drag)
    const handleTouchStartLightbox = useCallback((e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            // Pinch gesture start
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            setLastPinchDistance(distance);
        } else if (e.touches.length === 1) {
            // Check for double tap
            const now = Date.now();
            if (now - lastTapTime < 300) {
                handleDoubleTap(e.touches[0].clientX, e.touches[0].clientY);
                setLastTapTime(0);
                return;
            }
            setLastTapTime(now);

            // Single touch - either swipe or drag
            if (zoomState.scale > 1) {
                // Drag mode when zoomed
                setIsDragging(true);
                setDragStart({
                    x: e.touches[0].clientX - zoomState.translateX,
                    y: e.touches[0].clientY - zoomState.translateY,
                });
            } else {
                // Swipe mode
                setTouchStart(e.touches[0].clientX);
            }
        }
    }, [lastTapTime, zoomState.scale, zoomState.translateX, zoomState.translateY, handleDoubleTap]);

    const handleTouchMoveLightbox = useCallback((e: React.TouchEvent) => {
        if (e.touches.length === 2 && lastPinchDistance !== null) {
            // Pinch zoom
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            const delta = distance - lastPinchDistance;
            const scaleDelta = delta * 0.01;

            setZoomState(prev => {
                const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.scale + scaleDelta));
                if (newScale === 1) {
                    return { scale: 1, translateX: 0, translateY: 0 };
                }
                return { ...prev, scale: newScale };
            });

            setLastPinchDistance(distance);
        } else if (e.touches.length === 1 && isDragging && zoomState.scale > 1) {
            // Drag when zoomed
            const newX = e.touches[0].clientX - dragStart.x;
            const newY = e.touches[0].clientY - dragStart.y;

            const maxPan = (zoomState.scale - 1) * 150;
            setZoomState(prev => ({
                ...prev,
                translateX: Math.max(-maxPan, Math.min(maxPan, newX)),
                translateY: Math.max(-maxPan, Math.min(maxPan, newY)),
            }));
        }
    }, [lastPinchDistance, isDragging, dragStart, zoomState.scale]);

    const handleTouchEndLightbox = useCallback((e: React.TouchEvent) => {
        setLastPinchDistance(null);

        if (isDragging) {
            setIsDragging(false);
            return;
        }

        // Swipe navigation (only when not zoomed)
        if (touchStart !== null && zoomState.scale === 1 && e.changedTouches.length === 1) {
            const touchEnd = e.changedTouches[0].clientX;
            const diff = touchStart - touchEnd;

            if (Math.abs(diff) > 50) {
                if (diff > 0) {
                    goToNext();
                } else {
                    goToPrevious();
                }
            }
        }

        setTouchStart(null);
    }, [touchStart, zoomState.scale, goToNext, goToPrevious, isDragging]);

    // Keyboard navigation for lightbox
    useEffect(() => {
        if (!isLightboxOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowLeft':
                    if (zoomState.scale === 1) goToPrevious();
                    break;
                case 'ArrowRight':
                    if (zoomState.scale === 1) goToNext();
                    break;
                case '+':
                case '=':
                    handleZoomIn();
                    break;
                case '-':
                    handleZoomOut();
                    break;
                case '0':
                    resetZoom();
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isLightboxOpen, zoomState.scale, goToPrevious, goToNext, handleZoomIn, handleZoomOut, resetZoom]);

    if (!images || images.length === 0) {
        return (
            <div
                className="aspect-square w-full overflow-hidden rounded-lg bg-muted flex items-center justify-center text-muted-foreground/70"
                role="img"
                aria-label="No product images available"
            >
                No Images
            </div>
        );
    }

    return (
        <>
            {/* Main Gallery */}
            <div className="flex flex-col gap-4">
                {/* Main Image / Video */}
                <div className="relative group">
                    {isSelectedVideo ? (
                        <div className="aspect-square w-full overflow-hidden rounded-lg bg-muted border border-border flex items-center justify-center">
                            <video
                                key={selectedMedia}
                                src={selectedMedia}
                                controls
                                playsInline
                                preload="metadata"
                                className="h-full w-full object-contain object-center"
                                aria-label={`${title} - Video ${selectedIndex + 1}`}
                            />
                        </div>
                    ) : (
                        <div
                            ref={mainImageRef}
                            onMouseMove={handleMouseMove}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            className="relative"
                        >
                            <button
                                ref={lightboxTriggerRef}
                                onClick={() => handleLightboxOpenChange(true)}
                                className="aspect-square w-full overflow-hidden rounded-lg bg-muted border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                aria-label={`View ${title} in full screen. Image ${selectedIndex + 1} of ${images.length}`}
                            >
                                <img
                                    src={selectedMedia}
                                    alt={`${title} - Image ${selectedIndex + 1}`}
                                    className="h-full w-full object-contain object-center transition-transform duration-300"
                                />
                                {/* Zoom indicator on hover (hidden when magnifier is active) */}
                                <div className={cn(
                                    "absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center",
                                    showMagnifier && "opacity-0"
                                )}>
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
                                        <ZoomIn className="h-5 w-5 text-foreground" />
                                    </div>
                                </div>
                            </button>

                            {/* Magnifying Glass - Desktop only */}
                            {showMagnifier && (
                                <div
                                    className="hidden md:block fixed w-48 h-48 rounded-full border-4 border-white shadow-2xl pointer-events-none z-50 overflow-hidden"
                                    style={{
                                        left: magnifierPosition.x - 96,
                                        top: magnifierPosition.y - 96,
                                        backgroundImage: `url(${selectedMedia})`,
                                        backgroundSize: '400%',
                                        backgroundPosition: `${magnifierPosition.bgX}% ${magnifierPosition.bgY}%`,
                                        backgroundRepeat: 'no-repeat',
                                    }}
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                    )}

                    {/* Media counter badge */}
                    {images.length > 1 && (
                        <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full pointer-events-none">
                            {selectedIndex + 1} / {images.length}
                        </div>
                    )}
                </div>

                {/* Thumbnails */}
                {images.length > 1 && (
                    <div
                        className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
                        role="tablist"
                        aria-label="Product media thumbnails"
                    >
                        {images.map((image, index) => {
                            const isVideo = isVideoUrl(image);
                            return (
                                <button
                                    key={index}
                                    role="tab"
                                    aria-selected={selectedIndex === index}
                                    aria-label={`View ${isVideo ? 'video' : 'image'} ${index + 1} of ${images.length}`}
                                    className={cn(
                                        "relative flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20 cursor-pointer overflow-hidden rounded-md border bg-muted",
                                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                        "transition-all duration-200",
                                        selectedIndex === index
                                            ? "ring-2 ring-ring ring-offset-2 border-primary"
                                            : "border-border hover:border-border opacity-70 hover:opacity-100"
                                    )}
                                    onClick={() => handleImageChange(index)}
                                >
                                    {isVideo ? (
                                        <>
                                            <video
                                                src={`${image}#t=0.5`}
                                                muted
                                                preload="metadata"
                                                className="h-full w-full object-cover object-center"
                                                aria-hidden="true"
                                            />
                                            {/* Play icon overlay */}
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                <div className="w-6 h-6 bg-white/90 rounded-full flex items-center justify-center">
                                                    <Play className="h-3 w-3 text-foreground ml-0.5" fill="currentColor" />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <img
                                            src={image}
                                            alt=""
                                            aria-hidden="true"
                                            className="h-full w-full object-cover object-center"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <Dialog open={isLightboxOpen} onOpenChange={handleLightboxOpenChange}>
                <DialogContent
                    className="inset-0 left-0 top-0 h-screen max-h-none w-screen max-w-none translate-x-0 translate-y-0 border-none bg-black/95 p-0 text-white shadow-none sm:rounded-none data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0"
                    onTouchStart={handleTouchStartLightbox}
                    onTouchMove={handleTouchMoveLightbox}
                    onTouchEnd={handleTouchEndLightbox}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    <DialogTitle className="sr-only">{`${title} media viewer`}</DialogTitle>
                    <DialogDescription className="sr-only">
                        {`Viewing media ${selectedIndex + 1} of ${images.length} for ${title}. Use arrow keys to navigate, +/- to zoom, double-click or pinch to zoom.`}
                    </DialogDescription>

                    {/* Zoom controls */}
                    {!isSelectedVideo && (
                        <div className="absolute top-4 left-1/2 z-20 -translate-x-1/2 flex items-center gap-2 bg-black/60 rounded-full px-3 py-2">
                            <button
                                onClick={handleZoomOut}
                                disabled={zoomState.scale <= MIN_ZOOM}
                                className="p-1 rounded-full hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                aria-label="Zoom out"
                                type="button"
                            >
                                <ZoomOut className="h-5 w-5" />
                            </button>
                            <span className="text-sm font-medium min-w-[3rem] text-center">
                                {Math.round(zoomState.scale * 100)}%
                            </span>
                            <button
                                onClick={handleZoomIn}
                                disabled={zoomState.scale >= MAX_ZOOM}
                                className="p-1 rounded-full hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                aria-label="Zoom in"
                                type="button"
                            >
                                <ZoomIn className="h-5 w-5" />
                            </button>
                            {zoomState.scale > 1 && (
                                <button
                                    onClick={resetZoom}
                                    className="p-1 rounded-full hover:bg-white/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white ml-1"
                                    aria-label="Reset zoom"
                                    type="button"
                                >
                                    <RotateCcw className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    )}

                    {images.length > 1 && zoomState.scale === 1 && (
                        <button
                            onClick={goToPrevious}
                            className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white/90 transition-colors hover:bg-black/70 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                            aria-label="Previous media"
                            type="button"
                        >
                            <ChevronLeft className="h-8 w-8" />
                        </button>
                    )}

                    <div
                        ref={lightboxImageRef}
                        className={cn(
                            "flex h-full w-full items-center justify-center px-6 pb-24 pt-16 sm:px-12",
                            zoomState.scale > 1 && "cursor-grab",
                            isDragging && "cursor-grabbing"
                        )}
                        onMouseDown={!isSelectedVideo ? handleMouseDown : undefined}
                        onMouseMove={!isSelectedVideo ? handleMouseMoveZoom : undefined}
                        onDoubleClick={!isSelectedVideo ? (e) => handleDoubleTap(e.clientX, e.clientY) : undefined}
                    >
                        {isSelectedVideo ? (
                            <video
                                key={selectedMedia}
                                src={selectedMedia}
                                controls
                                playsInline
                                preload="metadata"
                                className="max-h-full max-w-full object-contain"
                                aria-label={`${title} - Video ${selectedIndex + 1}`}
                            />
                        ) : (
                            <img
                                src={selectedMedia}
                                alt={`${title} - Full size image ${selectedIndex + 1}`}
                                className="max-h-full max-w-full object-contain select-none"
                                style={{
                                    transform: `scale(${zoomState.scale}) translate(${zoomState.translateX / zoomState.scale}px, ${zoomState.translateY / zoomState.scale}px)`,
                                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                                }}
                                draggable={false}
                            />
                        )}
                    </div>

                    {images.length > 1 && zoomState.scale === 1 && (
                        <button
                            onClick={goToNext}
                            className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white/90 transition-colors hover:bg-black/70 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                            aria-label="Next media"
                            type="button"
                        >
                            <ChevronRight className="h-8 w-8" />
                        </button>
                    )}

                    {images.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/60 px-4 py-2 text-sm text-white">
                            {selectedIndex + 1} / {images.length}
                        </div>
                    )}

                    {images.length > 1 && zoomState.scale === 1 && (
                        <div className="absolute bottom-16 left-1/2 z-10 flex max-w-[90vw] -translate-x-1/2 gap-2 overflow-x-auto pb-2 scrollbar-hide">
                            {images.map((image, index) => {
                                const isVideo = isVideoUrl(image);
                                return (
                                    <button
                                        key={index}
                                        className={cn(
                                            "relative h-12 w-12 flex-shrink-0 overflow-hidden rounded border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white",
                                            selectedIndex === index
                                                ? "border-white opacity-100"
                                                : "border-transparent opacity-60 hover:opacity-85"
                                        )}
                                        onClick={() => handleImageChange(index)}
                                        aria-label={`Go to ${isVideo ? 'video' : 'image'} ${index + 1}`}
                                        type="button"
                                    >
                                        {isVideo ? (
                                            <>
                                                <video
                                                    src={`${image}#t=0.5`}
                                                    muted
                                                    preload="metadata"
                                                    className="h-full w-full object-cover"
                                                    aria-hidden="true"
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                    <div className="flex h-4 w-4 items-center justify-center rounded-full bg-white/90">
                                                        <Play className="ml-px h-2 w-2 text-foreground" fill="currentColor" />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <img
                                                src={image}
                                                alt=""
                                                aria-hidden="true"
                                                className="h-full w-full object-cover"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Zoom hint for mobile */}
                    {!isSelectedVideo && zoomState.scale === 1 && (
                        <div className="absolute bottom-28 left-1/2 z-10 -translate-x-1/2 text-xs text-white/60 text-center md:hidden">
                            Double-tap or pinch to zoom
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
